import { cloneElement, forwardRef } from 'react';
import { ClickableElement } from '../../../buttons/clickable-element/clickable-element.component';
import { MaybeLabel } from '../../../common-components/label/label.component';
import { BaseTextInput, type BaseTextInputProps } from '../base-input/base-input.component';

export type TextInputProps = Omit<BaseTextInputProps, 'leading' | 'trailing' | 'ariaLabel'> & {
	helpTextTooltip?: React.ReactElement | string;
	leadingConstantClassName?: string;
	trailingConstantClassName?: string;
	leadingConstant?: string | JSX.Element;
	trailingConstant?: string | JSX.Element;
	padIcon?: boolean;
	iconOnClick?: () => void;
	maskContent?: boolean;
} & (
		| {
				label: string; // if label specified, aria-label is optional
				ariaLabel?: string;
		  }
		| {
				label?: string;
				ariaLabel: string;
		  }
	);

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
	(
		{
			className = '',
			id,
			label,
			helpTextTooltip,
			required,
			icon,
			ariaLabel,
			iconOnClick,
			disabled,
			leadingConstant,
			trailingConstant,
			borderDark,
			padIcon = true,
			maskContent,
			leadingConstantClassName = '',
			trailingConstantClassName = '',
			size = 'MEDIUM',
			...remainingProps
		},
		ref
	) => {
		const iconPadding = size === 'LARGE' ? ' ph3 theme-luxury:pr-6! theme-luxury:pl-0!' : ' ph3 theme-luxury:pr-4! theme-luxury:pl-0!';
		const iconToRender = icon
			? cloneElement(icon, {
					className: `${icon.props.className} ${padIcon ? iconPadding : ''} self-center theme-luxury:text-2xl! ${
						disabled ? 'theme-luxury:text-disabled!' : 'theme-luxury:text-brand-primary!'
					} theme-luxury:hover:text-tertiary! theme-luxury:focus:text-secondary! theme-luxury:active:text-secondary!
					`
			  })
			: undefined;
		const renderIcon: JSX.Element | undefined =
			icon && iconOnClick ? (
				<ClickableElement
					disabled={disabled}
					className="self-center flex items-center theme-luxury:text-3xl! theme-luxury:text-brand-primary! theme-luxury:hover:text-tertiary! theme-luxury:focus:text-secondary! theme-luxury:active:text-secondary!"
					ariaLabel={`${ariaLabel}-icon`}
					onClick={iconOnClick}>
					{iconToRender}
				</ClickableElement>
			) : (
				iconToRender
			);

		const constantClassName = `ph2 bg-theme-grey-lighter br2 f6 theme-grey-darker flex items-center ${
			borderDark ? 'b--theme-grey-darker' : 'b--theme-grey-light'
		}`;

		const luxuryLeadingMargin = size === 'LARGE' ? 'theme-luxury:ml-6!' : 'theme-luxury:ml-4!';
		const disabledClassName = disabled
			? 'theme-luxury:bg-disabled! theme-luxury:text-placeholder-subtle!'
			: 'theme-luxury:bg-transparent!';
		const leading = leadingConstant ? (
			<div
				className={`${constantClassName} br--left br ${disabledClassName} theme-luxury:border-0! theme-luxury:pr-0.5! theme-luxury:text-quaternary! theme-luxury:text-base! theme-luxury:font-light! ${luxuryLeadingMargin} ${leadingConstantClassName}`}>
				{leadingConstant}
			</div>
		) : undefined;

		const luxuryTrailingPadding = size === 'LARGE' ? 'theme-luxury:pr-6!' : 'theme-luxury:pr-4!';
		const trailing = trailingConstant ? (
			<div
				className={`${constantClassName} br--right bl ${disabledClassName} theme-luxury:bg-transparent! theme-luxury:border-0! theme-luxury:text-quaternary! theme-luxury:text-base! theme-luxury:font-light! ${luxuryTrailingPadding} ${trailingConstantClassName} theme-luxury:pl-0!`}>
				{trailingConstant}
			</div>
		) : undefined;

		return (
			<MaybeLabel className={className} label={label} id={id} required={required} helpTextTooltip={helpTextTooltip}>
				<div className="relative w-100 font-medium">
					<BaseTextInput
						id={id}
						className={label ? undefined : className}
						inputClassName={`ph3 theme-luxury:pr-2! theme-luxury:py-2! theme-luxury:text-primary-500! theme-luxury:font-light! ${
							leadingConstant ? 'theme-luxury:pl-0!' : ''
						}`.trim()}
						required={required}
						icon={renderIcon}
						ref={ref}
						trailing={trailing}
						leading={leading}
						disabled={disabled}
						borderDark={borderDark}
						ariaLabel={ariaLabel}
						maskContent={maskContent}
						size={size}
						{...remainingProps}
					/>
				</div>
			</MaybeLabel>
		);
	}
);
